import { createContext, useCallback, useContext, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { PatientProfile } from "@/portals/patient/api/patientProfileApi";
import { accountProfileQuery, patientKeys } from "@/portals/patient/api/patientQueries";
import { usePatientAuthStore } from "@/stores/patientAuthStore";
import { useProxiedImage } from "@/shared/hooks/useProxiedImage";
import type { UserProfile } from "@/shared/types";

interface PatientProfileContextValue {
  profile: PatientProfile;
  setProfile: (profile: PatientProfile) => void;
  refreshProfile: () => Promise<void>;
  // Fetched ONCE here (not per-component) — every consumer that needs the patient's
  // avatar reads this instead of independently calling useProxiedImage(profile.avatarUrl),
  // so 3 render locations no longer trigger 3 separate network fetches that resolve
  // out of sync with each other.
  avatarSrc: string;
}

const PatientProfileContext = createContext<PatientProfileContextValue | null>(null);

// Only firstName/lastName come from the real logged-in user (via authUser.name) at this
// point — gender/dateOfBirth/memberSince aren't part of the login response, so seeding them
// from the sample fallbackProfile would flash demo data (e.g. "Female · Age 40 · Jun 1985")
// before the real fetch below lands and blanks them out for an actual new account.
function toPatientProfile(fallback: UserProfile, email?: string): PatientProfile {
  return {
    id: fallback.id,
    firstName: fallback.firstName,
    middleName: null,
    lastName: fallback.lastName,
    email: email ?? "",
    pendingEmail: null,
    address: "",
    phone: "",
    phoneVerified: false,
    gender: null,
    dateOfBirth: null,
    memberSince: "",
    avatarKey: undefined,
    avatarUrl: undefined,
    ssnProvidedAt: null,
  };
}

export function PatientProfileProvider({
  fallbackProfile,
  children,
}: {
  fallbackProfile: UserProfile;
  children: React.ReactNode;
}) {
  const authUser = usePatientAuthStore((s) => s.user);
  const client = useQueryClient();
  // The real profile from the shared query cache (retried on failure; kept
  // fresh by setProfile after a save, refreshProfile, and window focus).
  const query = useQuery(accountProfileQuery());

  // Until the fetch lands: the logged-in user's own name and email, nothing
  // else (see toPatientProfile).
  const placeholder = useMemo(() => {
    const [firstName, ...lastNameParts] = (authUser?.name ?? "").trim().split(/\s+/).filter(Boolean);
    return toPatientProfile(
      {
        ...fallbackProfile,
        firstName: firstName || fallbackProfile.firstName,
        lastName: lastNameParts.join(" ") || fallbackProfile.lastName,
      },
      authUser?.email,
    );
  }, [authUser?.name, authUser?.email, fallbackProfile]);

  const profile = query.data ?? placeholder;

  const setProfile = useCallback(
    (next: PatientProfile) => client.setQueryData(patientKeys.account, next),
    [client],
  );
  const { refetch } = query;
  const refreshProfile = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const avatarSrc = useProxiedImage(profile.avatarUrl);

  const value = useMemo(
    () => ({ profile, setProfile, refreshProfile, avatarSrc }),
    [profile, setProfile, refreshProfile, avatarSrc],
  );

  return (
    <PatientProfileContext.Provider value={value}>
      {children}
    </PatientProfileContext.Provider>
  );
}

export function usePatientProfile() {
  const context = useContext(PatientProfileContext);
  if (!context) throw new Error("usePatientProfile must be used inside PatientProfileProvider");
  return context;
}
